import React, { useRef, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Button, Chip, DateIntervalField, Grid, MenuItem, SelectField, Box, Stack, Typography, DateTimeStandard } from "@curves/ui-base";
import { displayDateText, getCurrentEndDay, getCurrentStartDate, parseDate, getToday, getSubMonths } from "@util/moment";
import { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";
import { callRef, ensureArray, isFunction, refIsRequiredError } from "@util/utils";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";
import useClubStockOutApi from "@apis/useClubStockOutApi";
import { Overview, ActionMenu } from '@curves/ui-shared';
import { ShipmentDialogBox } from "@clubInventory/shipment/dialog";
import { statusColorByValue } from "@clubInventory/shipment/ShipmentOverviewPage.config";

// 功能：預設排序欄位代碼
const defaultSortBy = 1;

// 功能：出貨單總覽表格欄位定義
const headerRow = [
	{
		cellKey: "stockOutNo",
		headerLabel: "出貨單編號",
		align: "center",
		formatCell: (stockOutNo, { clubStockOutID, stockOutStatus, stockOutStatusText }) => (
			<Stack direction="column" alignItems="center" justifyContent="center" spacing={0.5}>
				<Button component={NavLink} to={CLUB_INVENTORY_ROUTER.SHIPMENT.GET_DETAIL_PATH(clubStockOutID)} variant="text" color="primary">
					{stockOutNo}
				</Button>
				{stockOutStatus != null && <Chip label={stockOutStatusText} color={statusColorByValue[stockOutStatus] ?? "default"} />}
			</Stack>
		),
	},
	{
		cellKey: "stockOutTypeText",
		headerLabel: "出貨類型",
	},
	{
		cellKey: "targetClubName",
		headerLabel: "收貨對象",
	},
	{
		cellKey: "stockOutCreateDate",
		headerLabel: "建單日期",
		align: "center",
		formatCell: value => parseDate(value, DateTimeStandard.DisplayDateBySlash),
	},
	// 暫不顯示出貨日期
	// {
	// 	cellKey: "shipmentDate",
	// 	headerLabel: "出貨日期",
	// 	align: "center",
	// 	formatCell: value => value ? parseDate(value, DateTimeStandard.DisplayDateBySlash) : "—",
	// },
	{
		cellKey: "employeeName",
		headerLabel: "擔當",
	},
	{
		cellKey: "reason",
		headerLabel: "原因",
		minWidth: 200,
		formatCell: value => (
			<Typography variant="body2" className="mb-0">
				{value || "-"}
			</Typography>
		),
	},
];

// 功能：出貨單總覽（搜尋、列表、開啟店鋪調撥/瑕疵退貨 Dialog）
const ShipmentOverviewPage = () => {
	const { getClubStockOutOptionsApi, postClubStockOutOverviewApi } = useClubStockOutApi();
	const { options } = useOptions({
		getOptionsApi: getClubStockOutOptionsApi,
		isFetchOnMount: true,
	});
	const { overviewData, searchParams, getOverviewData, updateOverviewPage, updateOverviewSort } = useOverview({
		getOverviewApi: postClubStockOutOverviewApi,
		defaultSearchParams: useMemo(
			() => {
				const today = getToday();
				return {
					pageIndex: 1,
					pageSize: 10,
					sortBy: defaultSortBy,
					startDate: getCurrentStartDate(getSubMonths(today, 3)),
					endDate: getCurrentEndDay(today),
					stockOutType: 99,
					stockOutStatus: 99,
				};
			},
			// eslint-disable-next-line
			[],
		),
		isFetchOnMount: true,
	});

	// 功能：搜尋表單與彈窗 ref（供 getResult / 開啟 Dialog 使用）
	const dateRef = useRef(null);
	const typeRef = useRef(null);
	const statusRef = useRef(null);
	const dialogBoxRef = useRef(null);

	// 功能：組合目前篩選條件為查詢參數
	const getParamsData = () => {
		let result = {
			pageIndex: searchParams?.pageIndex || 1,
			pageSize: searchParams?.pageSize || 50,
			sortBy: searchParams?.sortBy || defaultSortBy,
		};

		const dateRange = callRef(dateRef, "getResult");
		if (dateRange) {
			const { startDate, endDate } = dateRange;
			result.startDate = getCurrentStartDate(startDate);
			result.endDate = getCurrentEndDay(endDate);
		}

		const type = callRef(typeRef, "getResult");
		if (type !== undefined && type !== null) result.stockOutType = type;

		const status = callRef(statusRef, "getResult");
		if (status !== undefined && status !== null) result.stockOutStatus = status;

		return result;
	};

	// 功能：搜尋送出（驗證必填後打 API 取得總覽）
	const handleSearchSubmit = () => {
		const paramsData = getParamsData();
		const isError = refIsRequiredError(dateRef, typeRef, statusRef);
		if (!isError) {
			return paramsData;
		}
		return false;
	};

	// 功能：開啟店鋪調撥 Dialog（成功後刷新列表）
	const handleOpenStoreTransferDialog = () => {
		if (isFunction(dialogBoxRef.current?.openStoreTransferDialog)) {
			dialogBoxRef.current.openStoreTransferDialog({ employeeOptions: [], storeOptions: [] }, () => getOverviewData(getParamsData()));
		}
	};

	// 功能：開啟瑕疵退貨 Dialog（成功後刷新列表）
	const handleOpenDefectReturnDialog = () => {
		if (isFunction(dialogBoxRef.current?.openDefectReturnDialog)) {
			dialogBoxRef.current.openDefectReturnDialog({ employeeOptions: [], receiverLabel: "總部" }, () => getOverviewData(getParamsData()));
		}
	};

	// 功能：Overview context（列表資料/分頁/排序/重新查詢）
	const contextValue = useMemo(
		() => ({
			overviewData,
			searchParams,
			getOverviewData,
			updateOverviewPage,
			updateOverviewSort,
		}),
		[overviewData, searchParams, getOverviewData, updateOverviewPage, updateOverviewSort],
	);

	return (
		<Box className="px-3 py-2">
			<Stack justifyContent="space-between" alignItems="center" className="mb-2">
				<Typography variant="h5" className="mb-0 font-weight-bold">
					出貨單總覽
				</Typography>
				<ActionMenu
					buttonText="建立"
					menuData={[
						{ text: "店鋪調撥", onClick: handleOpenStoreTransferDialog },
						{ text: "瑕疵退貨", onClick: handleOpenDefectReturnDialog },
					]}
				/>
			</Stack>
			<Overview contextValue={contextValue} idKey="clubStockOutID">
				{/* 搜尋區 */}
				<Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
					<Grid container spacing={2}>
						<Grid xs={6} item>
							<DateIntervalField
								label={"建單日期區間"}
								key={displayDateText(searchParams?.startDate, searchParams?.endDate)}
								ref={dateRef}
								startDateProps={{
									defaultValue: searchParams?.startDate || "",
								}}
								endDateProps={{
									defaultValue: searchParams?.endDate || "",
								}}
								fullWidth
							/>
						</Grid>
						<Grid xs={3} item>
							<SelectField label={"類型"} key={searchParams?.stockOutType} ref={typeRef} defaultValue={searchParams?.stockOutType} fullWidth required>
								{ensureArray(options?.stockOutTypeOptions).map(({ text, value, disabled }) => (
									<MenuItem key={value} value={value} disabled={disabled}>
										{text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
						<Grid xs={3} item>
							<SelectField label={"狀態"} key={searchParams?.stockOutStatus} ref={statusRef} defaultValue={searchParams?.stockOutStatus} fullWidth required>
								{ensureArray(options?.stockOutStatusOptions).map(({ text, value, disabled }) => (
									<MenuItem key={value} value={value} disabled={disabled}>
										{text}
									</MenuItem>
								))}
							</SelectField>
						</Grid>
					</Grid>
					<Box className="mt-3 text-right">
						<Button variant="contained" type="submit">
							搜尋
						</Button>
					</Box>
				</Overview.Search>

				{/* 清單資訊 */}
				<Stack direction="row" justifyContent="end" alignItems="center" spacing={2} className="mb-2">
					<Overview.TotalCount className="mb-0" showCurrentCount />
					{ensureArray(options?.stockOutSortByOption).length > 0 && (
						<SelectField key={searchParams?.sortBy} defaultValue={searchParams?.sortBy} onChange={e => updateOverviewSort(e.target.value)}>
							{ensureArray(options?.stockOutSortByOption).map(({ text, value }) => (
								<MenuItem key={value} value={value}>
									{text}
								</MenuItem>
							))}
						</SelectField>
					)}
				</Stack>

				{/* 列表區 */}
				<Overview.Table className="mb-3" headerRow={headerRow} />

				{/* 分頁 */}
				<Overview.Pagination />
			</Overview>
			<ShipmentDialogBox ref={dialogBoxRef} />
		</Box>
	);
};

export default ShipmentOverviewPage;
